import axios from "axios";
import { APP } from "../app/appConfig";
import { visibleNodes } from "../utils/visibleNodes";
import { LANGUAGE_CODES } from "../utils/languages";
import type { AnyGraphNode } from "../types/graphNodes";

/**
 * Catalog scoping. bm sends NO `a` param: verified 2026-07-12 that
 * `?platform=web4` and `?platform=web4&a=bm` redirect to the same S3 dump
 * (BhajanMala_prod_en.json), so adding `a=bm` would change the CloudFront and
 * browser cache keys for zero benefit. Do not "clean this up".
 */
const appParams = APP.apiApp ? { a: APP.apiApp } : {};

export type NodeType = { name: string; nodeTypeId: string };

// The browsable content catalog: every node (bhajans, collections, deities,
// composers, ragams, talams) except the lyrics text itself.
export type Catalog = { nodeTypes: NodeType[]; nodes: AnyGraphNode[] };

// Raw shape of the /api/graph/allNonLyrics response.
interface CatalogResponse {
  nodeTypes: NodeType[];
  nodes: AnyGraphNode[];
  hasMore?: boolean;
  nextFetchTimestamp?: number;
  deletedNodes?: { nodeId: number }[];
}

// In-memory cache for the catalog.
let cachedData: Catalog | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

// Prevent concurrent fetches
let fetchInProgress: Promise<Catalog> | null = null;

// Per-node full-data cache for detail hydration (language-trimmed server-side,
// so it MUST be cleared when the language changes).
const nodeCache = new Map<number, AnyGraphNode>();

// Current language for API requests
let currentLanguage = 'en';

export const updateLanguageAPI = (language: string) => {
  const langCode = LANGUAGE_CODES[language];
  if (langCode && langCode !== currentLanguage) {
    currentLanguage = langCode;
    // Clear cache when language changes
    cachedData = null;
    cacheTimestamp = 0;
    fetchInProgress = null;
    nodeCache.clear();
  }
};

/**
 * Fetch the slim browse/search index: every non-lyrics node as
 * {nodeId, nodeTypeId, name, normalizedName, edges, domains}. ~177 KB gzipped.
 * Same 5-min cache + language-clear semantics as the old fetchCatalog, but a
 * single request (no delta) — the index is small enough to re-fetch whole.
 */
export async function fetchIndex(forceRefresh = false): Promise<Catalog> {
  const now = Date.now();
  if (!forceRefresh && cachedData && (now - cacheTimestamp) < CACHE_DURATION_MS) {
    return cachedData;
  }
  if (fetchInProgress) return fetchInProgress;

  fetchInProgress = (async () => {
    try {
      const response = await axios.get<CatalogResponse>('/api/graph/index', {
        params: { platform: 'web4', l: currentLanguage, ...appParams },
      });
      const nodes = response.data.nodes || [];
      const nodeTypes = response.data.nodeTypes || [];
      // Strip "zzz…" placeholder/test entries — same guarantee as before.
      cachedData = { nodeTypes, nodes: visibleNodes(nodes) };
      cacheTimestamp = Date.now();
      return cachedData;
    } finally {
      fetchInProgress = null;
    }
  })();

  return fetchInProgress;
}

/**
 * Fetch full node data (all fields) for the given ids via the batch endpoint,
 * caching by nodeId. Used to hydrate detail-only fields (talam.beatPattern,
 * track/album audio, bb.ytLink, collection.artUrl) that the slim index omits.
 * Returns nodes in the requested order, skipping any the server didn't return.
 */
export async function fetchNodes(ids: number[]): Promise<AnyGraphNode[]> {
  const missing = ids.filter((id) => !nodeCache.has(id));
  if (missing.length > 0) {
    const response = await axios.get<AnyGraphNode[]>('/api/graph/nodes/multi', {
      params: { nodeIds: missing.join(','), platform: 'web4', l: currentLanguage, ...appParams },
    });
    for (const n of response.data || []) {
      if (n && typeof n.nodeId === 'number') nodeCache.set(n.nodeId, n);
    }
  }
  return ids.map((id) => nodeCache.get(id)).filter(Boolean) as AnyGraphNode[];
}

/**
 * By-id fetches are NOT domain-filtered by the server: measured 2026-07-12,
 * /api/graph/nodes/:id returns byte-identical payloads under platform=web,
 * platform=web4, +a=ss and +a=bm, for bm-only, ss-only and shared nodes alike.
 * `platform=web` (Omni) is therefore both correct and the safer value here —
 * it is the superset. Changing it would move bm's cache keys for no gain.
 */
export const fetchNodeById = async (nodeId: number) => {
  const response = await axios.get(`/api/graph/nodes/${nodeId}?platform=web`);
  return response.data;
};

export interface PlaylistResponse {
  id: string;
  name: string;
  bhajanIds: number[];
}

/** Fetch a shared playlist (name + ordered bhajan node ids) by its Firestore doc id. */
export const fetchPlaylist = async (id: string): Promise<PlaylistResponse> => {
  const response = await axios.get<PlaylistResponse>(`/api/playlists/${encodeURIComponent(id)}`);
  return response.data;
};
